import { useCallback, useEffect, useMemo } from 'react';

import { useIsMountedGetter } from './useIsMountedGetter';
import { useLatest } from './useLatest';
import {
	useDebouncedCallback,
	useThrottledCallback,
} from './useRateLimitedCallback';

interface Options {
	interval?: number;
	rateLimitType?: 'debounce' | 'throttle';
}

/**
 * Get a rate limited resize observer that disconnects on unmount.
 *
 * @example
 *
 * const { observer, restartObserver } = useResizeObserver(
 * 	 (obs: ResizeObserver) => {
 *     items.forEach((item) => {
 *       obs.observe(item);
 *     });
 * 	 },
 * 	 (entries: ResizeObserverEntry[]) => {
 * 	   setItemSizes(entries.map((entry) => entry.contentRect.width));
 * 	 },
 * );
 *
 * useEffect(() => {
 *   restartObserver();
 * }, [items.length]);
 */
export function useResizeObserver(
	observe: (observer: ResizeObserver) => void,
	callback: (entries: ResizeObserverEntry[]) => void,
	{ interval, rateLimitType = 'debounce' }: Options = {},
) {
	const isMounted = useIsMountedGetter();
	const latestObserve = useLatest(observe);
	const latestCallback = useLatest(callback);

	const defaultInterval = rateLimitType === 'throttle' ? 250 : 150;
	const useRateLimiter =
		rateLimitType === 'throttle' ? useThrottledCallback : useDebouncedCallback;

	const [limitedCallback] = useRateLimiter((entries: ResizeObserverEntry[]) => {
		// The component can be unmounted while waiting for the
		// debounce/throttle timeout to finish.
		if (isMounted()) {
			latestCallback.current(entries);
		}
	}, interval ?? defaultInterval);

	const observer = useMemo(
		() =>
			// Not available on the server.
			typeof ResizeObserver === 'undefined'
				? undefined
				: new ResizeObserver(limitedCallback),
		[limitedCallback],
	);

	useEffect(() => {
		if (observer) {
			latestObserve.current(observer);
		}
		return () => {
			observer?.disconnect();
		};
	}, [latestObserve, observer]);

	const restartObserver = useCallback(() => {
		if (observer) {
			observer.disconnect();
			latestObserve.current(observer);
		}
	}, [latestObserve, observer]);

	return { observer, restartObserver } as const;
}

/**
 * Use a rate limited resize observer to detect size changes for an element.
 */
export function useElementResize<T extends HTMLElement>(
	getElement: () => T | null,
	onResize: (entry: ResizeObserverEntry & { target: T }) => void,
	options?: Options,
) {
	return useResizeObserver(
		(obs: ResizeObserver) => {
			const el = getElement();
			if (el) {
				obs.observe(el);
			}
		},
		([entry]) => {
			if (entry?.target) {
				onResize(entry as ResizeObserverEntry & { target: T });
			}
		},
		options,
	);
}
